import CommandIdEnum from "../enums/CommandIdEnums";
import CanvasInputListener from "../logic/CanvasInputListener";
import Document from "../model/document/Document";

import Command from "./Command";
import AddLengthDimensionCmd from "./AddLengthDimensionCmd";
import AddTextAnnotationCmd from "./AddTextAnnotationCmd";
import RemoveFromDocumentCmd from "./RemoveFromDocumentCmd";
import AddChecklistItemCmd from "./AddChecklistItemCmd";
import FocusOnSceneObjectCmd from "./FocusOnSceneObjectCmd";
import FocusOnBimObjectCmd from "./FocusOnBimObjectCmd";
import GeometryMoveEditCmd from "./GeometryMoveEditCmd";
import GeometryDirectionalMoveCmd from "./GeometryDirectionalMoveCmd";
import GeometryPlanarMoveCmd from "./GeometryPlanarMoveCmd";
import GeometryFreeMoveCmd from "./GeometryFreeMoveCmd";
import ChecklistFreeMoveCmd from "./ChecklistFreeMoveCmd";
import GeometryDimensionEndpointMoveCmd from "./GeometryDimensionEndpointMoveCmd";
import GeometryPolygonEndpointMoveCmd from "./GeometryPolygonEndpointMoveCmd";
import SelectCmd from "./SelectCmd";
import AddAreaAnnotationCmd from "./AddAreaAnnotationCmd";
import AddRectangleAnnotationCmd from "./AddRectangleAnnotationCmd";
import AddCircleAnnotationCmd from "./AddCircleAnnotationCmd";
import DeleteSceneObjectCmd from "./DeleteSceneObjectCmd";
import ExtrudedLoopAreaSetExtrusionDepthCmd from "./ExtrudedLoopAreaSetExtrusionDepthCmd";
import MoveSceneObjectsCmd from "./MoveSceneObjectsCmd";
import CloneSceneObjectsCmd from "./CloneSceneObjectsCmd";
import BundleInspectorCmd from "./BundleInspectorCmd";
import DetachBundleElementsCmd from "./DetachBundleElementsCmd";
import { useServicesState } from "../store/servicesStateStore";

const REQUIRES_DOCUMENT = new Set<CommandIdEnum>([
    CommandIdEnum.Select,
    CommandIdEnum.AddLengthDimension,
    CommandIdEnum.AddTextAnnotation,
    CommandIdEnum.RemoveFromDocument,
    CommandIdEnum.AddChecklistItem,
    CommandIdEnum.FocusOnSceneObject,
    CommandIdEnum.GeometryMoveEdit,
    CommandIdEnum.GeometryDirectionalMove,
    CommandIdEnum.GeometryPlanarMove,
    CommandIdEnum.GeometryFreeMove,
    CommandIdEnum.ChecklistFreeMove,
    CommandIdEnum.AddAreaAnnotation,
    CommandIdEnum.AddRectangleAnnotation,
    CommandIdEnum.AddCircleAnnotation,
    CommandIdEnum.GeometryDimensionEndpointMove,
    CommandIdEnum.GeometryPolygonEndpointMove,
    CommandIdEnum.DeleteSceneObject,
    CommandIdEnum.ExtrudedLoopAreaSetExtrusionDepth,
    CommandIdEnum.MoveSceneObjects,
    CommandIdEnum.CloneSceneObjects
]);

const createCommand = ( commandId: CommandIdEnum,
                        canvasInputListener: CanvasInputListener,
                        document?: Document) : Command | null => {

    const { commandStack } = useServicesState.getState();

    if (REQUIRES_DOCUMENT.has(commandId) && !document) {
        console.error(`Command ${CommandIdEnum[commandId]} requires a document`);
        return null;
    }

    switch (commandId) {

        case CommandIdEnum.Select:
            return new SelectCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.AddLengthDimension:
            return new AddLengthDimensionCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.AddTextAnnotation:
            return new AddTextAnnotationCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.RemoveFromDocument:
            return new RemoveFromDocumentCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.AddChecklistItem:
            return new AddChecklistItemCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.FocusOnSceneObject:
            return new FocusOnSceneObjectCmd(commandStack, document!);

        case CommandIdEnum.FocusOnBimObject:
            return new FocusOnBimObjectCmd(commandStack);

        case CommandIdEnum.GeometryMoveEdit:
            return new GeometryMoveEditCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.GeometryDirectionalMove:
            return new GeometryDirectionalMoveCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.GeometryPlanarMove:
            return new GeometryPlanarMoveCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.GeometryFreeMove:
            return new GeometryFreeMoveCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.ChecklistFreeMove:
            return new ChecklistFreeMoveCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.AddAreaAnnotation:
            return new AddAreaAnnotationCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.AddRectangleAnnotation:
            return new AddRectangleAnnotationCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.AddCircleAnnotation:
            return new AddCircleAnnotationCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.GeometryDimensionEndpointMove:
            return new GeometryDimensionEndpointMoveCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.GeometryPolygonEndpointMove:
            return new GeometryPolygonEndpointMoveCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.DeleteSceneObject:
            return new DeleteSceneObjectCmd(commandStack, document!);

        case CommandIdEnum.ExtrudedLoopAreaSetExtrusionDepth:
            return new ExtrudedLoopAreaSetExtrusionDepthCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.MoveSceneObjects:
            return new MoveSceneObjectsCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.CloneSceneObjects:
            return new CloneSceneObjectsCmd(commandStack, canvasInputListener, document!);

        case CommandIdEnum.BundleInspector:
            return new BundleInspectorCmd(commandStack, canvasInputListener);

        case CommandIdEnum.DetachBundleElements:
            return new DetachBundleElementsCmd(commandStack, canvasInputListener);

        default:
            console.error(`Attempting to create command that has not been implemented`);
            return null;
    }

};

export default createCommand;
